/* State machine hook for the OPML import flow (podcasts or RSS feeds).
   Manages steps: instructions → upload → importing → results. */

import { useState, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { parseOpml, type ParsedPodcast } from "@/lib/parseOpml";
import { useFeatureTracking } from "@/hooks/useFeatureTracking";

export type SourceImportType = "podcast" | "rss";
export type ImportStep = "instructions" | "upload" | "importing" | "results";

interface ImportResults {
  imported: number;
  skipped: number;
  importedNames: string[];
}

const SOURCE_TYPE_FEATURE_MAP: Record<SourceImportType, string> = {
  podcast: "podcast_source_added",
  rss: "rss_source_added",
};

export function useFeedImport(sourceType: SourceImportType = "podcast") {
  const [step, setStep] = useState<ImportStep>("instructions");
  const [parsedFeeds, setParsedFeeds] = useState<ParsedPodcast[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [results, setResults] = useState<ImportResults | null>(null);

  const importMutation = useMutation(api.podcastImport.importPodcasts);
  const { trackFeature } = useFeatureTracking();

  const handleFileSelect = useCallback((file: File) => {
    setParseError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (typeof text !== "string") {
        setParseError("Could not read the file.");
        return;
      }
      const result = parseOpml(text);
      if (result.errors.length > 0) {
        setParseError(result.errors[0]);
        setParsedFeeds([]);
        return;
      }
      setParsedFeeds(result.podcasts);
    };
    reader.onerror = () => {
      setParseError("Failed to read the file.");
    };
    reader.readAsText(file);
  }, []);

  const handleImport = useCallback(async () => {
    if (parsedFeeds.length === 0) return;
    setStep("importing");
    try {
      const result = await importMutation({
        podcasts: parsedFeeds.map((p) => ({
          name: p.name,
          feedUrl: p.feedUrl,
        })),
        sourceType,
      });
      if (result.imported > 0) trackFeature(SOURCE_TYPE_FEATURE_MAP[sourceType]);
      setResults(result);
      setStep("results");
    } catch (err) {
      setParseError(
        err instanceof Error ? err.message : "Import failed. Please try again.",
      );
      setStep("upload");
    }
  }, [parsedFeeds, importMutation, sourceType]);

  const goToUpload = useCallback(() => {
    setStep("upload");
  }, []);

  const reset = useCallback(() => {
    setStep("instructions");
    setParsedFeeds([]);
    setParseError(null);
    setResults(null);
  }, []);

  return {
    step,
    parsedFeeds,
    parseError,
    isImporting: step === "importing",
    results,
    handleFileSelect,
    handleImport,
    goToUpload,
    reset,
  };
}

/** @deprecated Use useFeedImport instead */
export const usePodcastImport = () => useFeedImport("podcast");
